import { CollaborationChatRoom } from "types/chat"
import { Influencer, Post, SocialAccount, SocialPost } from "types/influencer"

export interface Collaborations {
    count: number,
    items: Collaboration[]
}

interface Campaign {
    uuid: string,
    name: string,
}

export interface Collaboration {
    status: string,
    total_earnings: string | null,
    influencer: Influencer,
    posts: CollaborationPost[],
    approved_posts_count: 0,
    views_count: number,
    interactions_count: number,
    cpe: string,
    cpv: string,
    application: any,
    active_milestone: Milestone,
    active_steps: MilestoneStep[],
    campaign: Pick<Campaign, "uuid" | "name">
}

export interface Milestone {
    id: number,
    sort_order: number,
    campaign_gigs: any[],
    gig_type: GigType,
    maximum_gigs: number,
    content_media_types: {
        REELS: string | number,
        FEED: string | number,
        STORY: string | number,
        VIDEO: string | number
    },
    specifications: MilestonSpecifications,
    brief: string,
    starts_at: string | null,
    ended_at: string | null,
    is_active: boolean,
    gig_amount: string | number,
    barter_amount: string | number,
    barter_reimbursement?: boolean,
    payment_days?: number,
    campaign_files: CampaignFile[],
    acknowledged_at?: string,
    feedbacks: MilestoneStepFeedback[]
    files: MilestoneFile[]
    hub_commission_rate?: {
        type: 'flat' | 'percent',
        value: string | number,
    } | null
    /** @deprecated API typo alias — prefer hub_commission_rate */
    hub_commision_rate?: Milestone['hub_commission_rate']
}



export interface CollaborationsSliceOld {
    error: string | null,
    loading: boolean,
    collaborationError: string | null,
    collaborationLoading: boolean,
    campaigns: Record<string, {
        total: number,
        filtered_lists: Record<string, {
            count: number,
            list: Collaboration[],
            page: number,
            has_more: boolean,
            last_fetched_at: number,
            filters: any,
            expired: boolean
        }>
        collaborations: Record<string, {
            collaboration: Collaboration,
            milestones: Milestone[]
            last_fetched_at: number,
        }>,
    }>,
    applied_filter_id: string | null,
    filters: CampaignCollaborationsFilters,
    tableFilters: CampaignCollaborationsFilters,
    collaborations: {
        defaultFilters: MilestoneCollaborationsFilters,
        applied_filter_id: string | null,
        total: number,
        filtered_lists: Record<string, {
            list: CollaborationItem[],
            count: number,
            page: number,
            last_fetched_at: number | null,
            expired: boolean,
            has_more: boolean
        }>
        loading: boolean,
        error: string | null,
    },
    pageLoading: boolean,

}

export interface CollaborationsSlice {
    filters: MilestoneCollaborationsFilters,
    applied_filter_id: string | null,
    total: number,
    filtered_lists: Record<string, {
        list: CollaborationItem[],
        count: number,
        page: number,
        last_fetched_at: number | null,
        expired: boolean,
        has_more: boolean
    }>
    items: Record<string, //CampaignId
        Record<string, //collaboratationId
            CollaborationItem>
    >,
    milestones: Record<string, //CampaignId
        Record<string, //collaborationId
            Record<string, {//MilestoneId
                milestone: MilestoneData,
                last_fetched_at: number
            }>
        >>,
    loading: boolean,
    itemLoading: boolean,
    error: string | null,
    summary: {
        data: CollaborationTasksSummary,
        last_fetched_at: number | null,
        loading: boolean,
        error: string | null
    },
    chatRooms: Record<string, //CampaignId
        Record<string, // CollaborationId
            {
                room: CollaborationChatRoom
                last_fetched_at: number,
            }
        >
    >,
    gigTypes: any[],
    gigSteps: any[]
}

export interface CollaborationTasksSummary {
    steps: {
        [key: string]: {
            name: string,
            count: number
        }
    }
}

export interface CollaborationItem {
    uuid: string,
    chat: CollaborationChatRoom,
    created: string,
    last_activity: string,
    influencer?: Influencer,
    social_account: SocialAccount,
    campaign: Pick<Campaign, "uuid" | "name">,
    active_milestone: Milestone,
    active_steps: MilestoneStep[],
    milestones: Milestone[],
    milestones_count: number
}

export enum SortOptions {
    MOST_RECENT = 'id',
    MOST_INTERACTIONS = 'interactions_count',
    MOST_VIEWS = 'views_count',
    LOWEST_CPV = 'cpv',
    LOWEST_CPE = 'cpe'
}

export const SortOptionLabel = {
    [SortOptions.MOST_RECENT]: 'Most Recent',
    [SortOptions.MOST_INTERACTIONS]: 'Most Interactions',
    [SortOptions.MOST_VIEWS]: 'Most Views',
    [SortOptions.LOWEST_CPV]: 'Lowest CPV',
    [SortOptions.LOWEST_CPE]: 'Lowest CPE'
}

export interface CampaignCollaborationsFilters {
    page: number,
    sort: SortOptions,
    order?: 'asc' | 'desc',
    [key: string]: any
}



export interface MilestonSpecifications {
    do: string[],
    dont: string[],
    talking_points?: TalkingPoint[],
    hashtags?: string[],
    mentions?: string[],
    collaborators?: string[],
    sample_scripts?: string[],
    sample_captions?: string[],
    [key: string]: any
}

export interface CampaignFile {
    type: string,
    url: string
}

export interface TalkingPoint {
    title: string,
    pointers: string[]
}

export interface GigType {
    id: number,
    slug: string,
    name: string,
    object_name: string,
    properties: GigTypeProperty[],
    has_options?: boolean
}

export interface GigTypeProperty {
    id: number,
    gig_type_id: number,
    label: string,
    key: string,
    type: string,
    sort_order: number,
    mandatory: boolean
}

export interface MilestoneStep {
    sort_order: number,
    is_locked: boolean,
    is_processed: boolean,
    processed: any,
    deliverables: {
        [key: string]: string
    },
    can_process: boolean,
    assignees?: string[],
    gig_step: GigStep,
    deadline: string | null,
    [key: string]: any
}

export interface MilestoneFile {
    step: string,
    uuid: string,
    url: string,
    type: string
}

export interface MilestoneStepFeedback {
    step: string,
    text: string,
    revision_required: boolean,
    created_at: string,
    reaction?: 'good' | 'better' | 'excellent'
}

export interface GigStep {
    slug: string,
    name: string,
    title: string,
    description: string,
    cta_text: string,
    revision_required_title: string,
    processed_title: string,
    object_name: string,
    properties: GigStepProperty[],
    attributes: Record<string, any>,
    require_deadline: boolean,
    non_assignee_title?: string | null,
    help_url?: string | null,
}

export interface GigStepProperty {
    label: string,
    key: string,
    type: 'boolean' | 'file' | 'large_file' | 'text' | 'textarea' | 'choice' | 'number' | 'wysiwyg' | 'address' | 'approval' | 'date' | 'datetime' | 'caption' | 'post',
    sort_order: number,
    mandatory: boolean,
    choices: {
        property: string
    },
    attributes: {
        max_file_size?: number,
        min_file_size?: number,
        accepted?: string[],
        [key: string]: any
    }
}

export interface GigStepPropertyWithValue extends GigStepProperty {
    value?: string | boolean | File | CTA,
    error?: string,
    progress?: number
}


export interface MilestoneData {
    milestone: Milestone,
    steps: MilestoneStep[],
    active_steps: MilestoneStep[],
    events: MilestoneEvent[]
    gigs: Gig[],
    posts: CollaborationPost[]
}

export interface MilestoneEvent {
    created: string,
    created_at: string,
    step: MilestoneStep,
    revision_required: boolean
    deliverables: any
    feedback?: MilestoneStepFeedback | null
}
export interface Gig {
    id: number,
    name: string,
    properties: Record<string, any>,
    value?: string
    thumbnail_url?: string,
    options?: CampaignGigOption[]
}


export interface CollaborationPost {
    status: "approved" | "rejected" | "pending",
    // ig_post: Post,
    social_post: SocialPost
}

export interface ProcessMilestonePayload {
    step: string,
    approval?: 'yes' | 'no'
    action?: 'accept' | 'reject',
    [key: string]: any
}

export interface MilestoneCollaborationsFilters {
    page: number,
    sort: MilestoneCollaborationSortOptions,
    status?: MilestoneCollaborationStatus,
    assigned_self?: 'y' | 'n',
    q?: string,
    [key: string]: any
}

export enum MilestoneCollaborationSortOptions {
    START_DATE = 'created_at',
    LAST_ACTIVITY_AT = 'last_activity_at'
}


export enum MilestoneCollaborationStatus {
    ONGOING = 'ongoing',
    COMPLETED = 'complete'
}

export const MilestoneCollaborationStatusLabel = {
    [MilestoneCollaborationStatus.ONGOING]: "Ongoing",
    [MilestoneCollaborationStatus.COMPLETED]: "Completed",
}

export const MilestoneCollaborationSortOptionLabel = {
    [MilestoneCollaborationSortOptions.START_DATE]: 'Start Date',
    [MilestoneCollaborationSortOptions.LAST_ACTIVITY_AT]: 'Last Activity'
}

export interface CTA {
    text: string,
    url?: string | null
}

export enum MilestoneStepAssignee {
    HUB = 'hub',
    INFLUENCER = 'influencer',
    AGENCY = 'agency'
}

export const MilestoneStepAssigneeLabel = {
    [MilestoneStepAssignee.HUB]: 'Me',
    [MilestoneStepAssignee.INFLUENCER]: 'Influencer',
    [MilestoneStepAssignee.AGENCY]: 'Agency'
}

export const MilestoneStepAssigneeColorCode = {
    [MilestoneStepAssignee.AGENCY]: 'dark-subtle',
    [MilestoneStepAssignee.HUB]: 'success',
    [MilestoneStepAssignee.INFLUENCER]: 'warning'
}

export interface CampaignGigOption {
    label: string,
    values: string[]
}